// monitoring-ui/components/CostsTab.tsx
import React, { useState } from 'react';
import { MetricCard, card, InsightBanner, Sparkline, Mono, badgeStyle, Spinner, EmptyState } from './MonitoringUi';
import { MonitoringService } from '../Service/MonitoringService';
import type { CostSummary, DeploymentCostRow, Recommendation } from '../Model/Monitoring';
import { color } from 'framer-motion';

interface Props {
  costSummary:     CostSummary | null;
  deploymentCosts: DeploymentCostRow[];
  isLoading:       boolean;
}

const recVariant = (r: Recommendation): 'red' | 'amber' | 'green' | 'blue' | 'gray' => {
  if (r === 'underprovisioned') return 'red';
  if (r === 'overprovisioned')  return 'amber';
  if (r === 'optimal')          return 'green';
  if (r === 'unknown')          return 'gray';
  return 'blue';
};

const recLabel = (r: Recommendation): string => {
  if (r === 'underprovisioned') return 'Needs upgrade';
  if (r === 'overprovisioned')  return 'Overprovisioned';
  if (r === 'optimal')          return 'Optimal';
  return 'Unknown';
};

export const CostsTab: React.FC<Props> = ({ costSummary, deploymentCosts, isLoading }) => {
  const [sending, setSending] = useState(false);
  const [sent, setSent]       = useState(false);

  const handleEmailReport = async () => {
    setSending(true);
    try {
      await MonitoringService.sendRightsizingDigest();
      setSent(true);
      setTimeout(() => setSent(false), 3000);
    } finally {
      setSending(false);
    }
  };

  if (isLoading || !costSummary) return <Spinner />;

  const costValues = costSummary.daily_series.map(d => d.cost);
  const paddedCostValues = costValues.length < 14
  ? [...Array(14 - costValues.length).fill(0), ...costValues]
  : costValues;
  const serviceEntries = Object.entries(costSummary.by_service).filter(([, v]) => v > 0);
  const savingsRow = deploymentCosts.filter(d => d.recommendation === 'overprovisioned' && d.potential_saving);

  return (
    <div>
      {/* Header cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '14px' }}>
        <MetricCard
          label="MTD spend"
          value={`$${costSummary.mtd_total.toLocaleString()}`}
          sub={costSummary.budget_usd ? `Budget $${costSummary.budget_usd.toLocaleString()}` : `${costSummary.deployment_count} deployments`}
        />
        <MetricCard
          label="Projected EOM"
          value={`$${costSummary.projected_eom.toLocaleString()}`}
          valueColor="#eab308"
          sub={<span style={{ color: '#f43f5e' }}>↑ 8% vs last month</span>}
        />
        <MetricCard
          label="Savings available"
          value={`$${costSummary.savings_available.toFixed(0)}`}
          valueColor="#22c55e"
          sub="AI rightsizing"
        />
        <MetricCard
          label="Avg per deployment"
          value={`$${costSummary.avg_cost_per_deployment.toFixed(0)}`}
          sub={`${costSummary.deployment_count} deployments`}
        />
      </div>

      {savingsRow.length > 0 && (
        <InsightBanner>
          <strong style={{ color: '#c7d2fe' }}>AI insight:</strong>{' '}
          {savingsRow.length} deployment{savingsRow.length > 1 ? 's are' : ' is'} over-provisioned.
          Rightsizing could save ~${costSummary.savings_available.toFixed(0)}/mo with no performance impact.{' '}
          <span
            style={{ textDecoration: 'underline', cursor: 'pointer', color: '#818cf8' }}
            onClick={() => document.getElementById('rightsizing-section')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Review ↗
          </span>
        </InsightBanner>
      )}

      {/* Charts row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
        <div style={card}>
          <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#52525f', marginBottom: '10px' }}>Daily spend (14d)</div>
          <Sparkline values={paddedCostValues} height={64} color="#6366f1" highlightLast />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#52525f', marginTop: '4px' }}>
            <span>{costSummary.daily_series[0]?.date?.slice(5)}</span>
            <span>{costSummary.daily_series[Math.floor(costSummary.daily_series.length / 2)]?.date?.slice(5)}</span>
            <span>Today</span>
          </div>
        </div>
        <div style={card}>
          <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#52525f', marginBottom: '10px' }}>Spend by service</div>
          {serviceEntries.map(([name, value], i) => {
            const colors = ['#6366f1', '#22c55e', '#eab308', '#818cf8'];
            return (
              <div key={name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: i < serviceEntries.length - 1 ? '1px solid #1a1a22' : 'none', fontSize: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '7px', color: '#eeeef5' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: colors[i % colors.length], display: 'inline-block' }} />
                  {name}
                </div>
                <span style={{ fontWeight: 500, color: '#eeeef5' }}>${value.toLocaleString()}</span>
              </div>
            );
          })}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0 0', borderTop: '1px solid #26262f', marginTop: '4px', fontSize: '12px' }}>
            <span style={{ fontWeight: 500, color: '#eeeef5' }}>MTD Total</span>
            <span style={{ fontWeight: 600, color: '#eeeef5' }}>${costSummary.mtd_total.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Per-deployment table */}
      <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#52525f', marginBottom: '8px' }}>Per-deployment breakdown</div>
      {deploymentCosts.length === 0 ? (
        <EmptyState message="No cost data available yet" />
      ) : (
        <div style={{ background: '#111116', border: '1px solid #26262f', borderRadius: '8px', overflow: 'hidden', marginBottom: '12px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', tableLayout: 'fixed' }}>
            <thead>
              <tr>
                {['App', 'Instance', 'MTD cost', 'Daily avg', 'Containers', 'Recommendation'].map((h, i) => (
                  <th key={i} style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#52525f', textAlign: 'left', padding: '8px 10px', background: '#0d0d12', borderBottom: '1px solid #26262f' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {deploymentCosts.map((row, i) => (
                <tr key={row.deployment_id}>
                  <td style={{ padding: '9px 10px', borderBottom: i < deploymentCosts.length - 1 ? '1px solid #1a1a22' : 'none' }}>
                    <div style={{ fontWeight: 500, fontSize: '12px', color:'#eeeef5' }}>{row.app_name ?? row.deployment_id}</div>
                    {row.repo_name && (
                      <div style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                        <Mono color="#52525f">{row.repo_name}:{row.branch}</Mono>
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '9px 10px', borderBottom: i < deploymentCosts.length - 1 ? '1px solid #1a1a22' : 'none' }}>
                    <Mono>{row.instance_type ?? '—'}</Mono>
                  </td>
                  <td style={{ color:'#eeeef5', padding: '9px 10px', fontWeight: 500, borderBottom: i < deploymentCosts.length - 1 ? '1px solid #1a1a22' : 'none' }}>
                    {row.estimated_cost_mtd != null ? `$${row.estimated_cost_mtd.toFixed(2)}` : '—'}
                  </td>
                  <td style={{ padding: '9px 10px', borderBottom: i < deploymentCosts.length - 1 ? '1px solid #1a1a22' : 'none', color: '#eeeef5' }}>
                    {row.estimated_cost_today != null ? `$${row.estimated_cost_today.toFixed(2)}` : '—'}
                  </td>
                  <td style={{ color:'#eeeef5', padding: '9px 10px', borderBottom: i < deploymentCosts.length - 1 ? '1px solid #1a1a22' : 'none' }}>
                    {row.container_count ?? '—'}
                  </td>
                  <td style={{ padding: '9px 10px', borderBottom: i < deploymentCosts.length - 1 ? '1px solid #1a1a22' : 'none' }}>
                    <span style={{ ...badgeStyle(recVariant(row.recommendation)), fontSize: '10.5px' }}>
                      {recLabel(row.recommendation)}
                    </span>
                    {row.potential_saving != null && row.potential_saving > 0 && (
                      <span style={{ fontSize: '10.5px', color: '#22c55e', marginLeft: '5px' }}>
                        saves ${row.potential_saving.toFixed(0)}/mo
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Rightsizing section */}
      <div id="rightsizing-section" style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#52525f', marginBottom: '8px' }}>
        Rightsizing recommendations
      </div>
      {savingsRow.length === 0 ? (
        <EmptyState icon="ti-mood-happy" message="All deployments are right-sized" />
      ) : (
        savingsRow.map((row) => (
          <div key={row.deployment_id} style={{ background: 'rgba(234,179,8,.04)', border: '1px solid rgba(234,179,8,.2)', borderRadius: '8px', padding: '12px 14px', marginBottom: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontWeight: 500, fontSize: '12.5px', marginBottom: '4px', color:'#eeeef5' }}>
                  {row.app_name ?? row.deployment_id}
                    {row.repo_name && (
                      <Mono color='#eeeef5'>
                        <span style={{ display: 'inline-block', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                          · {row.repo_name}:{row.branch}
                        </span>
                      </Mono>
                    )}
                </div>
                <div style={{ fontSize: '12px', color: '#eeeef5' }}>
                  <Mono>{row.instance_type}</Mono>
                  <span style={{ color: '#52525f', margin: '0 6px' }}>→</span>
                  <Mono color="#22c55e">{row.suggested_type}</Mono>
                  <span style={{ color: '#22c55e', marginLeft: '8px', fontWeight: 500 }}>
                    saves ${row.potential_saving?.toFixed(0)}/mo
                  </span>
                </div>
              </div>
              <span style={{ ...badgeStyle('amber'), fontSize: '10.5px' }}>Downsize available</span>
            </div>
          </div>
        ))
      )}

      {/* <div style={{ textAlign: 'center', paddingTop: '4px' }}>
        <button
          onClick={handleEmailReport}
          disabled={sending}
          style={{ background: 'transparent', border: '1px solid #26262f', color: sent ? '#22c55e' : '#eeeef5', borderRadius: '6px', padding: '6px 14px', fontSize: '12px', cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.6 : 1 }}
        >
          {sent ? '✓ Digest sent' : sending ? 'Sending…' : 'Email cost report ↗'}
        </button>
      </div> */}
    </div>
  );
};